import { useGetTools } from '@/modules/gpt-chats/hooks/use-tools';
import { toolsService } from '@/modules/gpt-chats/services/tools.service';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Card, CardContent } from '@/components/ui-kit/card';

import { cn } from '@/lib/utils';
import Link from '@/components/core/router-link';
import { Fragment } from 'react';
import { ToolsCard } from '@/modules/gpt-chats/components/tools/tool-card';
import { useQueryClient } from '@tanstack/react-query';
import { Pagination } from '@/components/ui-kit/pagination';
import { NoTools } from './no-tool';
import { ToolCardData } from '@/modules/gpt-chats/types/tools.service.type';
import { useTranslation } from 'react-i18next';

export const ToolsListSkelton = ({ view }: { view: string }) => {
  return (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <Fragment key={index}>
          {view === 'grid' ? (
            <Card className="flex flex-1 flex-col items-start gap-4 rounded border bg-card p-5 shadow">
              <div className="flex w-full items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                </div>
                <Skeleton className="h-5 w-5" />
              </div>
              <Skeleton className="h-10 w-full" />
            </Card>
          ) : (
            <Card className="flex h-[72px] items-start gap-4 self-stretch rounded border-0 bg-card p-3 shadow-none">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
                <Skeleton className="h-5 w-5" />
              </div>
            </Card>
          )}
        </Fragment>
      ))}
    </>
  );
};

export const ToolsList = ({
  offset,
  limit,
  view,
  toolType,
  onOffsetChange,
  onLimitChange,
  onAddClick,
}: {
  offset: number;
  limit: number;
  name: string;
  view: string | undefined;
  toolType: string;
  onOffsetChange: (offset: number) => void;
  onLimitChange?: (limit: number) => void;
  onAddClick?: () => void;
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const apiToolType =
    toolType === 'tool' ? 'api' : toolType === 'mcp-server' ? 'mcp_server' : toolType;

  const { data, isLoading, isFetching } = useGetTools({
    tool_type: apiToolType as 'api' | 'mcp_server',
    page: offset + 1,
    page_size: limit,
  });

  const tools = data?.tools || [];

  const regularTools = tools.filter((tool) => !tool.is_open_tool);
  const blocksTools = tools.filter((tool) => tool.is_open_tool);

  const handleToolClick = async (toolId: string) => {
    await queryClient.prefetchQuery({
      queryKey: ['tools', 'details', toolId],
      queryFn: () => toolsService.getToolDetails(toolId, false),
    });
  };

  const renderToolsGrid = (toolList: typeof tools, copiedBlocksTool: boolean) => (
    <div
      className={cn(
        'grid',
        view == 'grid' && 'grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3',
        view == 'list' && 'gap-1.5',
        toolList.length > 0 && view == 'grid' && 'min-h-[140px]',
        toolList.length > 0 && view == 'list' && 'min-h-[180px]',
        toolList.length > 0 && toolList.length <= 2 && view == 'grid' && 'auto-rows-auto',
        toolList.length > 2 && view == 'grid' && 'auto-rows-fr',
        view == 'list' && 'auto-rows-auto'
      )}
    >
      {toolList.map((tool, index: number) => {
        const detailsPath =
          tool.type === 'mcp_server'
            ? `/ai/mcp-server-details/${tool.id}/configuration?isBlocksTool=false&copiedBlocksTool=${copiedBlocksTool}`
            : `/ai/tool-details/${tool.id}/authentication?isBlocksTool=false&copiedBlocksTool=${copiedBlocksTool}`;

        const mappedTool: ToolCardData = {
          id: tool.id,
          name: tool.name,
          description: tool.description || '',
          status: tool.status,
          toolType: tool.type === 'mcp_server' ? 'mcp-server' : 'tool',
          is_open_tool: tool.is_open_tool,
        };

        return (
          <Link href={detailsPath} key={index} onClick={() => handleToolClick(tool.id)}>
            <ToolsCard
              tool={mappedTool}
              view={view as 'list' | 'grid'}
              copiedBlocksTool={copiedBlocksTool}
            />
          </Link>
        );
      })}
    </div>
  );

  return (
    <>
      <Card>
        <CardContent>
          {!isLoading && !isFetching && data && data.total_count === 0 ? (
            <NoTools onAddClick={onAddClick} isLoading={isLoading || isFetching} />
          ) : (
            <>
              {(isLoading || isFetching) && (
                <div
                  className={cn(
                    'grid',
                    view == 'grid' && 'grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3',
                    view == 'list' && 'gap-1.5'
                  )}
                >
                  <ToolsListSkelton view={view ?? 'list'} />
                </div>
              )}
              {!isFetching && (
                <>
                  {regularTools.length > 0 && (
                    <div className="mb-6">
                      <p className="border-b-1 border-gray-150 flex w-[100%] border-b pb-2 mt-2 mb-4 text-base font-normal text-low-emphasis">
                        {t('MY_TOOLS')}
                      </p>
                      {renderToolsGrid(regularTools, false)}
                    </div>
                  )}
                  {blocksTools.length > 0 && (
                    <div>
                      <p className="border-b-1 border-gray-150 flex w-[100%] border-b pb-2 mb-4 text-base font-normal text-low-emphasis">
                        {t('BLOCKS_TOOLS_TAB')}
                      </p>
                      {renderToolsGrid(blocksTools, true)}
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {data && data.total_count > 0 && (
            <div className="mt-5 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {t('SHOWING_ENTRIES', {
                  from: offset * limit + 1,
                  to: Math.min((offset + 1) * limit, data.total_count),
                  total: data.total_count,
                })}
              </span>
              {data.total_count > limit && (
                <Pagination
                  page={offset}
                  onChange={onOffsetChange}
                  onPageSizeChange={onLimitChange}
                  totalCount={data.total_count}
                  pageSizeOptions={[10, 50, 100]}
                  pageSize={limit}
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export const useToolsListData = ({
  offset,
  limit,
  toolType,
}: {
  offset: number;
  limit: number;
  toolType: string;
}) => {
  const apiToolType =
    toolType === 'tool' ? 'api' : toolType === 'mcp-server' ? 'mcp_server' : toolType;

  return useGetTools({
    tool_type: apiToolType as 'api' | 'mcp_server',
    page: Math.floor(offset / limit) + 1,
    page_size: limit,
  });
};
